import { useQuery } from "@tanstack/react-query";
import { apiCall } from "../lib/api";
import { escapeHtml } from "../lib/format";
import type { ChangeEntry } from "../lib/types";

export type ChangeHistoryTarget = { id: string; name?: string } | null;

// Ported verbatim from renderChangeHistory(). Returns an HTML string we inject
// via dangerouslySetInnerHTML so the .ch-* markup matches the legacy app exactly.
function renderChangeHistoryHTML(changes: ChangeEntry[]): string {
  if (!changes.length) return '<div class="ch-empty">No recorded changes for this order yet.</div>';
  const rows = changes
    .map((c) => {
      const when = String(c.changed_at || "").replace("T", " ").replace("Z", "") + " UTC";
      const actor = c.actor || "unknown";
      const actorCls = "ch-actor ch-actor-" + (c.actor_kind || "system");
      let head: string;
      if (c.action === "update") head = '<span class="ch-field">' + escapeHtml(c.field || "") + "</span> changed";
      else if (c.action === "create") head = "order created";
      else if (c.action === "delete") head = "order deleted";
      else if (c.action === "reprint") head = "form reprinted (REPRINT-UPDATE)";
      else head = escapeHtml(c.action || "changed");
      let diff = "";
      if (c.action === "update") {
        diff =
          '<div class="ch-diff"><span class="ch-old">' +
          escapeHtml(c.old_value == null ? "∅" : c.old_value) +
          '</span> <span class="ch-arrow">→</span> <span class="ch-new">' +
          escapeHtml(c.new_value == null ? "∅" : c.new_value) +
          "</span></div>";
      }
      const reason = c.reason ? '<div class="ch-reason">' + escapeHtml(c.reason) + "</div>" : "";
      const entity = c.entity === "film_drop" ? '<span class="ch-entity">drop form</span>' : "";
      return (
        '<div class="ch-item"><div class="ch-line">' +
        head +
        " " +
        entity +
        "</div>" +
        diff +
        reason +
        '<div class="ch-meta"><span class="' +
        actorCls +
        '">' +
        escapeHtml(actor) +
        "</span> · " +
        escapeHtml(when) +
        "</div></div>"
      );
    })
    .join("");
  return '<div class="ch-list">' + rows + "</div>";
}

export function ChangeHistoryModal({ target, onClose }: { target: ChangeHistoryTarget; onClose: () => void }) {
  const open = !!target;
  const { data, isLoading, error } = useQuery({
    queryKey: ["order-change-history", target?.id],
    queryFn: () => apiCall<{ changes: ChangeEntry[] }>(`order-change-history?id=${encodeURIComponent(target!.id)}`),
    enabled: open,
  });

  let bodyHTML: string;
  if (isLoading) bodyHTML = '<div class="ch-empty">Loading…</div>';
  else if (error) bodyHTML = '<div class="ch-empty">Could not load history: ' + escapeHtml((error as Error).message) + "</div>";
  else bodyHTML = renderChangeHistoryHTML(data?.changes || []);

  return (
    <div
      className={`tasks-modal${open ? " show" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="tasks-modal-content">
        <div className="tasks-modal-header">
          <span className="tasks-modal-title">{"Change history" + (target?.name ? " — " + target.name : "")}</span>
          <div className="tasks-modal-header-actions">
            <span className="tasks-modal-close" onClick={onClose}>
              &times;
            </span>
          </div>
        </div>
        <div className="tasks-modal-body" dangerouslySetInnerHTML={{ __html: bodyHTML }} />
      </div>
    </div>
  );
}
